import React, { useEffect, useState } from 'react';

import { refreshFileURL } from '../../services/conversations';
import { humanSize } from '../../services/files';
import { DownloadIcon, FileIcon } from './icons';

// What a tool produced, shown to the user. An image is the one thing worth
// rendering in the thread; everything else is a chip you can download, and a
// viewer for the ones we could preview (CSV, JSON) belongs in a side panel
// rather than in a chat bubble.

// AttachedImage renders the picture the model was also looking at.
//
// width and height matter more than they look: without them the tag mounts at
// no height, the thread lays out around nothing, and the picture shoves
// everything down when it decodes — which the viewport then chases. With them
// the browser reserves the right box up front, from the aspect ratio, still
// bounded by the max-* classes. They are absent for an SVG or a format we
// could not read, and then this behaves as it did before.
export function AttachedImage({ image, filename, fileId, width, height }) {
  const { url, failed, retry } = useFileURL(image, fileId);

  if (failed) {
    return <BrokenFile filename={filename} />;
  }

  return (
    <figure className="my-2">
      <a href={url} target="_blank" rel="noreferrer" title="Open full size">
        <img
          src={url}
          alt={filename || 'attachment'}
          width={width || undefined}
          height={height || undefined}
          onError={retry}
          className="h-auto max-h-80 max-w-full rounded-lg border border-gray-200 object-contain"
        />
      </a>

      <figcaption className="mt-1 flex items-center gap-2 text-xs text-gray-500">
        <span className="truncate">{filename}</span>
        <DownloadLink url={url} filename={filename} />
      </figcaption>
    </figure>
  );
}

// AttachedFile is everything we can't show: one chip, one download.
export function AttachedFile({ data, filename, mimeType, bytes, fileId }) {
  const { url, failed, retry } = useFileURL(data, fileId);

  if (failed) {
    return <BrokenFile filename={filename} />;
  }

  return (
    <div className="my-2 flex max-w-sm items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2">
      <FileIcon className="h-5 w-5 shrink-0 text-gray-400" />

      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-gray-800">{filename}</span>
        <span className="block truncate text-xs text-gray-500">
          {describe(mimeType, bytes)}
        </span>
      </span>

      <DownloadLink url={url} filename={filename} onError={retry} />
    </div>
  );
}

// download works because the file is served from our own origin; the attribute
// is ignored cross-origin, which is why the content route stays on /api/v1.
function DownloadLink({ url, filename }) {
  return (
    <a
      href={url}
      download={filename || true}
      title="Download"
      aria-label={`Download ${filename || 'file'}`}
      className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
    >
      <DownloadIcon className="h-4 w-4" />
    </a>
  );
}

function BrokenFile({ filename }) {
  return (
    <div className="my-2 flex max-w-sm items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500">
      <FileIcon className="h-5 w-5 shrink-0 text-gray-300" />
      <span className="truncate">{filename || 'file'} is no longer available</span>
    </div>
  );
}

// useFileURL keeps an expiring link invisible. A file link is minted per
// response and outlives a normal session, but a chat left open long enough
// will hold a stale one. One refetch covers that; a second failure is a real
// error and says so, instead of refetching forever.
function useFileURL(initial, fileId) {
  const [url, setURL] = useState(initial);
  const [failed, setFailed] = useState(false);
  const [retried, setRetried] = useState(false);

  // a new message part, or the same one re-rendered with a fresher link
  useEffect(() => {
    setURL(initial);
    setFailed(false);
    setRetried(false);
  }, [initial]);

  const retry = () => {
    if (retried || !fileId) {
      setFailed(true);
      return;
    }

    setRetried(true);

    refreshFileURL(fileId)
      .then((fresh) => (fresh ? setURL(fresh) : setFailed(true)))
      .catch(() => setFailed(true));
  };

  return { url, failed, retry };
}

function describe(mimeType, bytes) {
  const parts = [];

  if (mimeType) parts.push(mimeType);
  if (bytes > 0) parts.push(humanSize(bytes));

  return parts.join(' · ');
}
